import * as React from 'react';
import { Box, Stack } from "@mui/system"
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Button, FormControl, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailIcon from '@mui/icons-material/Email';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input'
import { formatCustomerName, formatPhoneNumber } from '../Utils';
import { CUSTOMER_INTEGRATION_ATTRIBUTES_FRIENDLY_NAMES, STATE_ABBREVIATIONS } from '../Constants';
import { MaterialUISwitchTextEmail } from '../Utils/MaterialUISwitch';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

export default function CustomerAccordion({ customer, default_expanded, editable, createNewCustomer, editingAlert, updateCustomer }) {
    const [expanded, setExpanded] = React.useState(default_expanded || createNewCustomer || false);
    const [my_customer, setCustomer] = React.useState(customer)
    const [editing, setEditing] = React.useState(createNewCustomer || false)

    const handleChange = (prop) => (event, extra) => {
        if (['phone'].includes(prop)) {
            if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
                setCustomer({ ...my_customer, [prop]: extra.nationalNumber })
                updateCustomer && updateCustomer({ ...my_customer, [prop]: extra.nationalNumber })
            }
        } else if (['zip'].includes(prop)) {
            if (/^\d{0,5}(-\d{0,4})?$/.test(event.target.value)) {
                setCustomer({ ...my_customer, [prop]: event.target.value })
                updateCustomer && updateCustomer({ ...my_customer, [prop]: event.target.value })
            }
        } else {
            setCustomer({ ...my_customer, [prop]: event.target.value })
            updateCustomer && updateCustomer({ ...my_customer, [prop]: event.target.value })
        }
    }

    const updateEditing = () => {
        if (editing) {
            setCustomer(customer)
        }
        editingAlert && editingAlert(!editing)
        setEditing(!editing)
    }

    React.useEffect(() => {
        setCustomer(customer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer])

    React.useEffect(() => {
        setEditing(createNewCustomer)
        if (createNewCustomer) {
            setExpanded(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createNewCustomer])

    let accordionHeader = ''
    if (expanded) {
        accordionHeader = 'Customer Details'
    } else {
        if (customer.id) {
            accordionHeader = `${formatCustomerName(customer)} ${formatPhoneNumber(customer.phone)}`
        } else {
            accordionHeader = 'Customer Details'
        }
    }

    return (
        (<Accordion disableGutters expanded={expanded} onChange={() => { if (!createNewCustomer && !editing) setExpanded(!expanded) }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="customeraccordion-content"
                id="customeraccordion-header"
            >
                <Typography>{accordionHeader}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box>
                    <Grid container spacing={0} columnSpacing={1} columns={12}>
                        <Grid container direction="column" size={8}>
                            <Grid container spacing={1} size="grow">
                                <Grid size={12}>
                                    <TextField
                                        margin="dense"
                                        id="name"
                                        label="Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={my_customer?.name ?? ""}
                                        onChange={handleChange("name")}
                                        disabled={!editing}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container size={'grow'}>
                                <Grid size={8} >
                                    <TextField
                                        margin="dense"
                                        id="address_1"
                                        label="Address 1"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={my_customer?.address_1 ?? ""}
                                        onChange={handleChange("address_1")}
                                        disabled={!editing}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container size={'grow'}>
                                <Grid size={8} >
                                    <TextField
                                        margin="dense"
                                        id="address_2"
                                        label="Address 2"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={my_customer?.address_2 ?? ""}
                                        onChange={handleChange("address_2")}
                                        disabled={!editing}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} size="grow">
                                <Grid size={5}>
                                    <TextField
                                        margin="dense"
                                        id="city"
                                        label="City"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={my_customer?.city ?? ""}
                                        onChange={handleChange("city")}
                                        disabled={!editing}
                                    />
                                </Grid>
                                <Grid size={"auto"}>
                                    <FormControl variant="filled" sx={{ mt: 1, minWidth: 80 }}>
                                        <Autocomplete
                                            options={STATE_ABBREVIATIONS}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            autoComplete
                                            includeInputInList
                                            disableClearable
                                            value={my_customer?.state ?? ''}

                                            onChange={(e, val) => setCustomer({ ...my_customer, state: val })}
                                            renderInput={(params) => {
                                                return (<TextField {...params} label="State" variant="filled" fullWidth autoComplete='off' />)
                                            }}
                                            renderOption={(props, option, { inputValue }) => {
                                                const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                                const parts = parse(props.key, matches);
                                                props.key = props.id

                                                return (
                                                    <li {...props} >
                                                        <div>
                                                            {parts.map((part, index) => (
                                                                <span
                                                                    key={index}
                                                                    style={{
                                                                        fontWeight: part.highlight ? 700 : 400,
                                                                    }}
                                                                >
                                                                    {part.text}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </li>
                                                );
                                            }}
                                            disabled={!editing}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={4}>
                                    <TextField
                                        margin="dense"
                                        id="zip"
                                        label="ZIP / Postal"
                                        type="number"
                                        inputMode="numeric"
                                        fullWidth
                                        variant="filled"
                                        value={my_customer?.zip ?? ""}
                                        onChange={handleChange("zip")}
                                        disabled={!editing}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} alignItems={'center'}>
                                <Grid size="auto">
                                    <span>Communication Preference:</span>
                                </Grid>
                                <Grid size="grow">
                                    {editing ? <Stack direction="row" component="label" alignItems="center" justifyContent="flex-begin">
                                        <Typography sx={{ fontWeight: 'bold' }}>Text</Typography>
                                        <MaterialUISwitchTextEmail onChange={(e) => setCustomer({ ...my_customer, preferred_communication_type: e.target.checked ? 'email' : 'text' })} checked={my_customer?.preferred_communication_type === 'email'} />
                                        <Typography sx={{ fontWeight: 'bold' }}>Email</Typography>
                                    </Stack>
                                        :
                                        <Box display={'flex'} sx={{ alignItems: 'center' }}>
                                            <Box display="flex" sx={{ bgcolor: 'primary.main', borderRadius: '50%', height: '32px', width: '32px', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                                {
                                                    my_customer?.preferred_communication_type === 'email' ?
                                                        <EmailIcon sx={{ height: '20px', width: '20px', color: '#fff' }} />
                                                        :
                                                        <PhoneAndroidIcon sx={{ height: '20px', width: '20px', color: '#fff' }} />
                                                }
                                            </Box>
                                            {
                                                my_customer?.preferred_communication_type === 'email' ?
                                                    <Typography sx={{ fontWeight: 'bold' }}>Email</Typography>
                                                    :
                                                    <Typography sx={{ fontWeight: 'bold' }}>Text</Typography>
                                            }
                                        </Box>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container direction="column" size={4}>
                            <Grid size="grow">
                                <TextField
                                    margin="dense"
                                    id="email"
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    variant="filled"
                                    value={my_customer?.email ?? ""}
                                    onChange={handleChange("email")}
                                    disabled={!editing}
                                />
                            </Grid>
                            <Grid size="grow">
                                <TextField
                                    margin="dense"
                                    id="company"
                                    label="Company"
                                    type="text"
                                    fullWidth
                                    variant="filled"
                                    value={my_customer?.company_name ?? ""}
                                    onChange={handleChange("company_name")}
                                    disabled={!editing}
                                />
                            </Grid>
                            <Grid size="grow">
                                <MuiTelInput
                                    margin="dense"
                                    id="phone"
                                    label="Phone"
                                    type="tel"
                                    fullWidth
                                    variant="filled"
                                    value={`+1 ${my_customer?.phone ?? ""}`}
                                    onChange={handleChange("phone")}
                                    disabled={!editing}
                                    InputLabelProps={{ shrink: true }}
                                    onlyCountries={['US']}
                                    defaultCountry="US"
                                    forceCallingCode
                                    disableDropdown
                                />
                            </Grid>
                            {Object.keys(my_customer?.integration_attrs ?? {}).map((attr) =>
                                <Grid size="grow" key={attr}>
                                    <span><b>{CUSTOMER_INTEGRATION_ATTRIBUTES_FRIENDLY_NAMES[attr]}:</b> {my_customer.integration_attrs[attr]}</span>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Box>
                {editable &&
                    <Box sx={{ textAlign: 'right' }}>
                        <Button variant="contained" onClick={updateEditing}>{editing ? "Cancel Edit" : "Edit"}</Button>
                    </Box>
                }
            </AccordionDetails>
        </Accordion >)
    );
}